/* Header Search Drawer
 * Right-side slide-in drawer that wraps Shopify's <predictive-search>.
 * Manages open/close, focus trap, and recent searches in localStorage.
 */

const RECENT_SEARCHES_KEY = 'reasn:recentSearches';
const RECENT_SEARCHES_MAX = 6;

function readRecentSearches() {
  try {
    const raw = localStorage.getItem(RECENT_SEARCHES_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((t) => typeof t === 'string') : [];
  } catch (e) {
    return [];
  }
}

function writeRecentSearches(list) {
  try {
    localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(list.slice(0, RECENT_SEARCHES_MAX)));
  } catch (e) {
    /* quota / disabled — ignore */
  }
}

function pushRecentSearch(term) {
  const clean = (term || '').trim();
  if (!clean) return;
  const lower = clean.toLowerCase();
  const existing = readRecentSearches().filter((t) => t.toLowerCase() !== lower);
  existing.unshift(clean);
  writeRecentSearches(existing);
  document.dispatchEvent(new CustomEvent('recent-searches:updated'));
}

function clearRecentSearches() {
  writeRecentSearches([]);
  document.dispatchEvent(new CustomEvent('recent-searches:updated'));
}

class SearchDrawer extends HTMLElement {
  constructor() {
    super();
    this.activeElement = null;
    this.input = this.querySelector('#SearchDrawer-Input');
    this.predictiveSearch = this.querySelector('predictive-search');
    this.emptyState = this.querySelector('[data-search-drawer-empty]');

    this.addEventListener('keyup', (e) => e.code === 'Escape' && this.close());
    this.querySelector('#SearchDrawer-Overlay').addEventListener('click', () => this.close());

    this.querySelectorAll('[data-search-drawer-close]').forEach((btn) =>
      btn.addEventListener('click', () => this.close())
    );

    document.querySelectorAll('[data-search-drawer-trigger]').forEach((trigger) => {
      trigger.addEventListener('click', (e) => {
        e.preventDefault();
        this.open(trigger);
      });
    });

    this.bindRecentSearches();
    this.bindResultLogging();
  }

  bindRecentSearches() {
    this.recentList = this.querySelector('[data-recent-searches-list]');
    this.recentSection = this.querySelector('[data-recent-searches-section]');

    const clearBtn = this.querySelector('[data-recent-searches-clear]');
    if (clearBtn) {
      clearBtn.addEventListener('click', () => clearRecentSearches());
    }

    document.addEventListener('recent-searches:updated', () => this.renderRecentSearches());

    // Trending chips also write to recent on click.
    this.querySelectorAll('[data-recent-search-write]').forEach((el) => {
      el.addEventListener('click', () => pushRecentSearch(el.getAttribute('data-recent-search-write')));
    });

    // Form submit -> push current term.
    const form = this.querySelector('form[role="search"]');
    if (form) {
      form.addEventListener('submit', () => pushRecentSearch(this.input.value));
    }

    // Live result clicks (delegated) -> push current term.
    if (this.predictiveSearch) {
      this.predictiveSearch.addEventListener('click', (e) => {
        const link = e.target.closest('a, button');
        if (!link) return;
        if (link.matches('.predictive-search__item, .predictive-search__item--term')) {
          pushRecentSearch(this.input.value);
        }
      });
    }
  }

  bindResultLogging() {
    if (!this.predictiveSearch) return;
    // Toggle empty state visibility based on whether predictive-search has results.
    const obs = new MutationObserver(() => this.syncEmptyState());
    obs.observe(this.predictiveSearch, { attributes: true, attributeFilter: ['open', 'results', 'loading'] });
    this.input.addEventListener('input', () => this.syncEmptyState());
  }

  syncEmptyState() {
    const hasQuery = this.input && this.input.value.trim().length > 0;
    if (!this.emptyState) return;
    this.emptyState.hidden = hasQuery;
  }

  renderRecentSearches() {
    if (!this.recentList || !this.recentSection) return;
    const items = readRecentSearches();
    if (items.length === 0) {
      this.recentSection.hidden = true;
      this.recentList.innerHTML = '';
      return;
    }
    this.recentSection.hidden = false;
    this.recentList.innerHTML = items
      .map((term) => {
        const safe = term.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
        const url = `${window.routes?.search_url || '/search'}?q=${encodeURIComponent(term)}&options%5Bprefix%5D=last`;
        return `<li>
          <a href="${url}" class="search-drawer__chip" data-recent-search-term="${safe}">${safe}</a>
          <button type="button" class="search-drawer__chip-remove" aria-label="Remove ${safe}" data-recent-search-remove="${safe}">&times;</button>
        </li>`;
      })
      .join('');

    this.recentList.querySelectorAll('[data-recent-search-remove]').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        const term = btn.getAttribute('data-recent-search-remove');
        const next = readRecentSearches().filter((t) => t.toLowerCase() !== term.toLowerCase());
        writeRecentSearches(next);
        document.dispatchEvent(new CustomEvent('recent-searches:updated'));
      });
    });
  }

  shuffleFeaturedProducts() {
    const grid = this.querySelector('[data-search-featured-grid]');
    if (!grid) return;
    const items = Array.from(grid.querySelectorAll('[data-search-featured-item]'));
    if (items.length === 0) return;
    // Fisher-Yates shuffle
    for (let i = items.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [items[i], items[j]] = [items[j], items[i]];
    }
    const visibleCount = 8;
    items.forEach((item, idx) => {
      item.hidden = idx >= visibleCount;
      grid.appendChild(item);
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.activeElement = triggeredBy;
    this.hidden = false;
    // Force reflow then animate.
    // eslint-disable-next-line no-unused-expressions
    this.offsetHeight;
    setTimeout(() => this.classList.add('animate', 'active'));

    document.body.classList.add('overflow-hidden');
    this.renderRecentSearches();
    this.shuffleFeaturedProducts();
    this.syncEmptyState();

    this.addEventListener(
      'transitionend',
      () => {
        if (!this.classList.contains('active')) return;
        if (typeof trapFocus === 'function') {
          trapFocus(this, this.input);
        } else if (this.input) {
          this.input.focus();
        }
      },
      { once: true }
    );
  }

  close() {
    this.classList.remove('active');
    document.body.classList.remove('overflow-hidden');
    if (typeof removeTrapFocus === 'function') {
      try {
        removeTrapFocus(this.activeElement);
      } catch (e) {
        /* ignore */
      }
    }
    setTimeout(() => {
      this.hidden = true;
    }, 300);
  }
}

customElements.define('search-drawer', SearchDrawer);
